import { clsx, type ClassValue } from 'clsx'
import { format, formatDistanceToNow } from 'date-fns'

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export type FormatCurrencyOptions = {
  /** Show up to 2 decimal places when the amount is not a whole rupee. */
  decimals?: 'auto'
}

export function normalizeMoneyAmount(amount: number): number {
  return Math.round(amount * 100) / 100
}

export function formatCurrency(
  amount: number,
  currency = 'INR',
  options?: FormatCurrencyOptions
): string {
  const normalized = normalizeMoneyAmount(amount)
  const showDecimals = options?.decimals === 'auto'

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(normalized)
}

export function formatDate(date: string | Date): string {
  return format(new Date(date), 'dd MMM yyyy')
}

export function formatDateTime(date: string | Date): string {
  return format(new Date(date), 'dd MMM yyyy, hh:mm a')
}

export function formatRelative(date: string | Date): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true })
}

export function stockLimitMessage(stock: number): string {
  if (stock <= 0) return 'Sorry, this item is currently out of stock.'
  if (stock === 1) return 'Sorry, we only have 1 item left in stock right now.'
  return `Sorry, we only have ${stock} items left in stock right now.`
}

export function generateSku(prefix = 'BEM'): string {
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase()
  return `${prefix}-${rand}`
}

export function generateOrderNumber(): string {
  const ts = Date.now().toString(36).toUpperCase()
  return `ORD-${ts}`
}

export function truncate(str: string, len: number): string {
  if (str.length <= len) return str
  return `${str.slice(0, len)}…`
}

export function paginate<T>(items: T[], page: number, perPage: number) {
  const start = (page - 1) * perPage
  return {
    data: items.slice(start, start + perPage),
    total: items.length,
    totalPages: Math.ceil(items.length / perPage),
    page,
    perPage,
  }
}
